// Persistência do contexto de entrega.
//
// Dois relógios distintos, e é o que justifica este helper existir:
// o CEP e o endereço escolhido valem 30 dias, porque o cliente não muda de endereço entre
// sessões; a resolução (farmácia, custo, prazo) vale 15 minutos, porque horário de
// funcionamento e estoque mudam ao longo do dia.
//
// Com a resolução vencida o CEP é preservado e só a resolução é refeita — o cliente não
// pode voltar a ver "informe seu CEP" por ter almoçado. Com o contexto vencido, some tudo.
//
// Escrita só em saga. Este módulo é chamado pelas sagas e, apenas para leitura, pelos
// INITIAL_STATE de deliveryContext e storeByUser — que precisam do valor de forma síncrona,
// antes de qualquer requisição de catálogo sair.

export const DELIVERY_CONTEXT_KEY = "@ConexaoGAM:deliveryContext";

export const TTL_LOCAL = 30 * 24 * 60 * 60 * 1000; // 30 dias — CEP e endereço
export const TTL_RESOLUCAO = 15 * 60 * 1000; // 15 minutos — farmácia, custo e prazo

const SCHEMA_VERSION = 1;

// Lê o contexto persistido, já validado.
//
// O relógio de 30 dias é conferido ANTES de qualquer outra coisa: contexto vencido não
// devolve nada, nem a farmácia eleita, mesmo com a chave ainda presente no navegador.
// A leitura vencida também limpa a chave, para não reavaliar o mesmo lixo a cada boot.
export function readDeliveryContext() {
  try {
    const raw = localStorage.getItem(DELIVERY_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== SCHEMA_VERSION || !parsed.cep) {
      localStorage.removeItem(DELIVERY_CONTEXT_KEY);
      return null;
    }

    const agora = Date.now();

    if (!parsed.definidoEm || agora - parsed.definidoEm > TTL_LOCAL) {
      localStorage.removeItem(DELIVERY_CONTEXT_KEY);
      return null;
    }

    // Só aqui, com o contexto já válido, o relógio curto entra em cena. Ele não descarta a
    // resolução: apenas informa que ela precisa ser refeita.
    const resolucaoVencida =
      !parsed.resolvidoEm || agora - parsed.resolvidoEm > TTL_RESOLUCAO;

    return {
      cep: parsed.cep,
      origem: parsed.origem || null,
      enderecoId: parsed.enderecoId ?? null,
      localidade: parsed.localidade || null,
      cobertura: parsed.cobertura || null,
      resolucao: parsed.resolucao || null,
      resolvidoEm: parsed.resolvidoEm || null,
      definidoEm: parsed.definidoEm,
      resolucaoVencida
    };
  } catch (err) {
    // Chave corrompida não pode derrubar o boot: o INITIAL_STATE roda em tempo de import.
    try {
      localStorage.removeItem(DELIVERY_CONTEXT_KEY);
    } catch (_) {}
    return null;
  }
}

// Farmácia eleita pela última resolução persistida, no formato que o duck storeByUser usa.
//
// Devolvida mesmo com a resolução vencida (desde que o contexto de 30 dias esteja válido):
// o TTL de 15 minutos existe por causa de prazo e estoque, não porque a farmácia que cobre
// um CEP mude em quinze minutos. Enquanto a nova resolução não chega, o catálogo continua
// recebendo store= em vez de perder o parâmetro e mudar de comportamento.
export function readResolvedStore() {
  const contexto = readDeliveryContext();
  if (!contexto || !contexto.resolucao) return null;

  const { lojas, eleita_loja_id } = contexto.resolucao.entrega || {};
  if (!eleita_loja_id || !Array.isArray(lojas)) return null;

  const eleita = lojas.find((loja) => loja.loja_id === eleita_loja_id);
  if (!eleita || !eleita.loja_uuid) return null;

  return {
    id: eleita.loja_id,
    uuid: eleita.loja_uuid,
    selecionadaPeloCliente: false
  };
}

// Grava o contexto. erroCep nunca entra aqui: erro de digitação não sobrevive à sessão.
export function writeDeliveryContext(state) {
  try {
    localStorage.setItem(
      DELIVERY_CONTEXT_KEY,
      JSON.stringify({
        v: SCHEMA_VERSION,
        cep: state.cep,
        origem: state.origem,
        enderecoId: state.enderecoId,
        localidade: state.localidade,
        cobertura: state.cobertura,
        resolucao: state.resolucao,
        resolvidoEm: state.resolvidoEm,
        definidoEm: state.definidoEm
      })
    );
  } catch (_) {
    // Cota estourada ou modo privativo: o contexto segue valendo em memória.
  }
}

export function clearDeliveryContext() {
  try {
    localStorage.removeItem(DELIVERY_CONTEXT_KEY);
  } catch (_) {}
}

// Normaliza para os oito dígitos que a API e o estado usam.
export function normalizeCep(cep) {
  const digits = String(cep || "").replace(/\D/g, "");
  return digits.length === 8 ? digits : null;
}
